import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';

const Booking = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    date: '',
    time: '',
    duration: '2',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const services = [
    'Standard Carpet Cleaning',
    'Premium Deep Cleaning',
    'Emergency Spot Cleaning',
    'Move-in/Move-out Cleaning',
    'Commercial Carpet Cleaning'
  ];

  const timeSlots = [
    '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM',
    '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Email.js configuration
    const serviceID = 'service_2hve1bh'; // Replace with your Email.js service ID
    const templateID = 'template_ea0ruyw'; // Replace with your Email.js template ID
    const publicKey = '0DJ-RHjNFtw-sl11i'; // Replace with your Email.js public key

    // Send email using Email.js
    emailjs.sendForm(serviceID, templateID, form.current, publicKey)
      .then((result) => {
        console.log('Email sent successfully:', result.text);
        setSubmitStatus('success');
        setIsSubmitting(false);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          service: '',
          date: '',
          time: '',
          duration: '2',
          message: ''
        });
      })
      .catch((error) => {
        console.error('Email sending failed:', error.text);
        setSubmitStatus('error');
        setIsSubmitting(false);
      });
  };

  // Calculate minimum date (tomorrow)
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <>
      <SEO 
        title="Book Carpet Cleaning Service Online - GreenRocky Carpet"
        description="Book your professional carpet cleaning service online. Choose your preferred date and time. Fast, easy scheduling with instant confirmation. Eco-friendly cleaning solutions."
        keywords="book carpet cleaning, schedule appointment, online booking, carpet cleaning appointment, service booking"
        url="https://greenrockycarpet.com.au/booking"
        section="Booking"
      />


    <div className="page-section">
      <h1 className="section-title">Book Your Service</h1>
      <p style={{textAlign: 'center', marginBottom: '50px', fontSize: '1.1rem'}}>
        Fill out the form below to schedule your carpet cleaning service. We'll contact you to confirm your appointment.
      </p>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="success-message">
          <div className="success-icon">✅</div>
          <div className="success-content">
            <h3>Booking Submitted Successfully!</h3>
            <p>Thank you for your booking. We have received your request and will contact you shortly to confirm your appointment.</p>
          </div>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="error-message">
          <div className="error-icon">❌</div>
          <div className="error-content">
            <h3>Booking Failed</h3>
            <p>There was an error submitting your booking. Please try again or contact us directly at <strong>+61 1300 989 330</strong>.</p>
          </div>
        </div>
      )}

      <form ref={form} className="booking-form" onSubmit={handleSubmit}>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px'}}>
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px'}}>
          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="service">Service Type *</label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
            >
              <option value="">Select a service</option>
              {services.map((service, index) => (
                <option key={index} value={service}>{service}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="address">Service Address *</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Full address including city and zip code"
            required
          />
        </div>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px'}}>
          <div className="form-group">
            <label htmlFor="date">Preferred Date *</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              min={minDate}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="time">Preferred Time *</label>
            <select
              id="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              required
            >
              <option value="">Select a time</option>
              {timeSlots.map((time, index) => (
                <option key={index} value={time}>{time}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="duration">Estimated Duration (hours)</label>
            <select
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
            >
              <option value="1">1 hour</option>
              <option value="2">2 hours</option>
              <option value="3">3 hours</option>
              <option value="4">4 hours</option>
              <option value="5">5+ hours</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="message">Additional Information</label>
          <textarea
            id="message"
            name="message"
            rows="4"
            value={formData.message}
            onChange={handleChange}
            placeholder="Any specific stains, room sizes, special requirements, etc."
          ></textarea>
        </div>

        <button 
          type="submit" 
          className="submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className="spinner"></div>
              Sending...
            </>
          ) : (
            'Book Appointment'
          )}
        </button>
      </form>

      <div style={{marginTop: '50px', textAlign: 'center'}}>
        <h3 style={{color: '#2e7d32', marginBottom: '20px'}}>Need Immediate Assistance?</h3>
        <p style={{marginBottom: '10px'}}>Call us directly: <strong>+61 1300 989 330</strong></p>
        <p>Emergency services available 24/7</p>
      </div>
    </div>
    </>
  );
};

export default Booking;